import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
  Flights,
  Prices,
  Slice,
  MulticitySlice,
  TripSlice,
  TripSummary,
  MulticityFlights,
} from "@b2bportal/air-shopping-api";
import type {
  IFlightListData,
  IFlightNumberFilter,
  ITimeRange,
} from "@b2bportal/core-types";
import { removeTimezone } from "@hopper-b2b/utilities";
import dayjs from "dayjs";

import {
  type AirlineCode,
  FlightRatingsEnum,
  SliceStopCountFilter,
} from "../../../../../types";

export const performStopOptionFilter = (
  flight: TripSlice,
  stopsOption: SliceStopCountFilter
): boolean => {
  switch (stopsOption) {
    case SliceStopCountFilter.ONE_OR_LESS:
      return flight.stops <= 1;
    case SliceStopCountFilter.TWO_OR_LESS:
      return flight.stops <= 2;
    case SliceStopCountFilter.NONE:
      return flight.stops === 0;
    default:
      return true;
  }
};

export const performMaxPriceFilter = (
  trip: TripSummary,
  maxPriceFilter: number
) => {
  return trip.tripFares.some(
    (fare) => fare.amount.fiat.value <= maxPriceFilter
  );
};

export const performTimeRangeFilter = (
  flight: TripSlice,
  departureTime: ITimeRange,
  arrivalTime: ITimeRange
) => {
  const flightDeparture = dayjs(removeTimezone(flight.departureTime));
  const flightArrival = dayjs(removeTimezone(flight.arrivalTime));

  const flightDepartureMinutes =
    flightDeparture.hour() * 60 + flightDeparture.minute();
  const flightArrivalMinutes =
    flightArrival.hour() * 60 + flightArrival.minute();

  const validDeparture =
    departureTime.max >= flightDepartureMinutes &&
    flightDepartureMinutes >= departureTime.min;
  const validArrival =
    arrivalTime.max >= flightArrivalMinutes &&
    flightArrivalMinutes >= arrivalTime.min;

  return validDeparture && validArrival;
};

export const performAirlineFilter = (
  flight: TripSlice,
  airlineFilter: AirlineCode[]
) => {
  return airlineFilter.includes(flight.segmentDetails[0].airlineCode);
};

export const performAirportFilter = (
  slice: TripSlice,
  airportFilter: string[]
) => {
  return airportFilter.includes(slice.originCode);
};

export const performFlightNumberFilter = (
  flight: TripSlice,
  flightNumberFilter: IFlightNumberFilter[]
) => {
  return flightNumberFilter.some((filter) => {
    const [firstSegment] = flight.segmentDetails;
    return (
      firstSegment.airlineCode === filter.airlineCode &&
      firstSegment.flightNumber === filter.flightNumber
    );
  });
};

export const performFareClassFilter = (
  flight: TripSummary,
  fareClassOptionFilter: string[],
  isReturn: boolean,
  outgoingRating?: number
) => {
  const getAllFlightRating = () => {
    return flight.tripFares.reduce<Record<string, boolean>>(
      (flightRating, fare) => {
        //Only add return rating if outgoing flight rating matches in the fare.
        const rating =
          isReturn &&
          (outgoingRating === undefined ||
            outgoingRating === fare.fareShelf?.outgoing.rating)
            ? fare.fareShelf?.returning?.rating
            : !isReturn
              ? fare.fareShelf?.outgoing.rating
              : null;

        //zero should not be considered as false
        if (rating || rating === 0) flightRating[rating] = true;
        return flightRating;
      },
      {}
    );
  };

  // If the flight has *any* of the users selected fare classes, return true.
  return fareClassOptionFilter.some((fare) => {
    return getAllFlightRating()[FlightRatingsEnum[fare.toLocaleLowerCase()]];
  });
};

///
export const performStopOptionFilterV2 = (
  flight: Slice | MulticitySlice,
  stopsOption: SliceStopCountFilter
): boolean => {
  switch (stopsOption) {
    case SliceStopCountFilter.ONE_OR_LESS:
      return flight.stops <= 1;
    case SliceStopCountFilter.TWO_OR_LESS:
      return flight.stops <= 2;
    case SliceStopCountFilter.NONE:
      return flight.stops === 0;
    default:
      return true;
  }
};

export const performMaxPriceFilterV2 = (
  fares: { amount: Prices }[],
  maxPriceFilter: number
) => {
  return fares.some((fare) => fare.amount.fiat.value <= maxPriceFilter);
};

export const performTimeRangeFilterV2 = (
  flight: Slice | MulticitySlice,
  departureTime: ITimeRange,
  arrivalTime: ITimeRange
) => {
  const flightDeparture = dayjs(removeTimezone(flight.departure));
  const flightArrival = dayjs(removeTimezone(flight.arrival));

  const flightDepartureMinutes =
    flightDeparture.hour() * 60 + flightDeparture.minute();
  const flightArrivalMinutes =
    flightArrival.hour() * 60 + flightArrival.minute();

  const validDeparture =
    departureTime.max >= flightDepartureMinutes &&
    flightDepartureMinutes >= departureTime.min;
  const validArrival =
    arrivalTime.max >= flightArrivalMinutes &&
    flightArrivalMinutes >= arrivalTime.min;

  return validDeparture && validArrival;
};

export const performAirlineFilterV2 = (
  flight: Slice | MulticitySlice,
  airlineFilter: AirlineCode[]
) => {
  return airlineFilter.includes(flight.segments[0].marketingAirline);
};

export const performAirportFilterV2 = (
  slice: Slice | MulticitySlice,
  airportFilter: string[]
) => {
  return (
    airportFilter.includes(slice.origin) ||
    airportFilter.includes(slice.destination)
  );
};

export const performFlightNumberFilterV2 = (
  flight: Slice | MulticitySlice,
  flightNumberFilter: IFlightNumberFilter[]
) => {
  return flightNumberFilter.some((filter) => {
    const [firstSegment] = flight.segments;
    return (
      firstSegment.marketingAirline === filter.airlineCode &&
      firstSegment.flightNumber === filter.flightNumber
    );
  });
};

export const performFareClassFilterV2 = (
  flights: Flights | MulticityFlights,
  flight: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>,
  fareClassOptionFilter: string[]
) => {
  return getFaresRating(flight.fares, flights).filter((fare) =>
    fareClassOptionFilter.includes(FlightRatingsEnum[fare.rating])
  );
};

const getFaresRating = (
  fares: (Fare | FareSliceOutbound | FareSliceDeparture)[],
  flights: Flights | MulticityFlights
) => {
  return fares.reduce(
    (flightRating, fare) => {
      const fareSliceId =
        "fareSlice" in fare ? fare.fareSlice : (fare as Fare).return;

      const amount =
        fare.amount ?? (fare as FareSliceOutbound).priceBreakdown?.taxesAndFees;

      if (fareSliceId === undefined || amount === undefined)
        return flightRating;

      const rating = flights.fareSlices[fareSliceId].fareShelf.value;

      if (rating || rating === 0) flightRating.push({ amount, rating });

      return flightRating;
    },
    [] as { amount: Prices; rating: number }[]
  );
};
